import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import cone from '../assets/da2026/cone.png';
import wordmarkLight from '../assets/da2026/abrf_wordmark_light.png';
import groupeClim from '../assets/da2026/partners/groupe-clim.png';
import temporis from '../assets/da2026/partners/temporis.png';
import tourtelTwist from '../assets/da2026/partners/tourtel-twist.png';
import bab2 from '../assets/da2026/partners/bab2.png';
import creditMutuel from '../assets/da2026/partners/credit-mutuel.png';

// Variantes de bandeau (marque) — sidestep des photos manquantes
const TILE_BANDS = [
  'from-[#F26522] to-[#D9531A]',
  'from-[#20364B] to-[#141312]',
  'from-[#F59B3B] to-[#D9531A]',
];

// Partenaire officiel de chaque tournoi. Les logos sont embarques dans le bundle
// (l'ancien champ `logo_partenaire` en base pointe vers un stockage Supabase en pause).
// On matche sur le slug, stable, avec repli sur le nom : jamais sur l'id numerique.
//
// La hauteur est propre a chaque logo, et ce n'est pas de la coquetterie : leurs
// proportions vont du carre (Tourtel Twist, 1:1) au bandeau (Credit Mutuel, 7.9:1).
// A hauteur egale, le bandeau occupait pres de 8 fois la surface du carre et
// l'ecrasait visuellement. On egalise donc la SURFACE plutot que la hauteur :
// pour une surface cible S, hauteur = racine(S / ratio). Les valeurs ci-dessous
// visent ~950 px², bornees a 13 px pour que le bandeau reste lisible et a 30 px
// pour que les carres ne fassent pas grossir le badge.
// Si un logo change, recalculer sa hauteur depuis les dimensions du fichier.
const PARTNERS = [
  { match: /entreprise/i,    name: 'Groupe Clim',   logo: groupeClim,   h: 'h-[30px]' }, // ratio 1.09
  { match: /before/i,        name: 'Temporis',      logo: temporis,     h: 'h-[17px]' }, // ratio 3.36
  { match: /iguane/i,        name: 'Tourtel Twist', logo: tourtelTwist, h: 'h-[30px]' }, // ratio 1.00
  { match: /mercader/i,      name: 'BAB2',          logo: bab2,         h: 'h-[17px]' }, // ratio 3.22
  { match: /neska|mutilak/i, name: 'Crédit Mutuel', logo: creditMutuel, h: 'h-[13px]' }, // ratio 7.87
];
const partnerFor = (t) =>
  PARTNERS.find(p => p.match.test(t.slug || '') || p.match.test(t.name || ''));

const Tournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        setLoading(true);
        let data, error;

        // Essayer d'abord avec 'tournoi' (singulier)
        const resultSingular = await supabase.from('tournoi').select('*').order('ordre');

        // Fallback sur toute erreur (table absente => '42P01' en SQL, 'PGRST205' via l'API PostgREST)
        if (resultSingular.error) {
          console.log(`Requête sur 'tournoi' en échec (${resultSingular.error.code}), tentative avec 'tournois'`);
          const resultPlural = await supabase.from('tournois').select('*').order('ordre');
          data = resultPlural.data;
          error = resultPlural.error;
        } else {
          data = resultSingular.data;
          error = resultSingular.error;
        }

        if (error) {
          setErrorDetails(`${error.message} (${error.code})`);
          throw error;
        }

        if (!data || data.length === 0) {
          setTournaments([]);
          return;
        }

        // Avancement reel de chaque tournoi, deduit des matchs plutot que de son nom
        // ou de la date inscrite dans sa description — les dates en base se sont
        // revelees peu fiables, les scores non.
        // Un match sans equipe1_texte est un emplacement encore vide (meme filtre que
        // la fiche tournoi) : il ne compte ni comme prevu, ni comme joue.
        // En cas d'echec de la requete on ne compte rien : aucun badge vaut mieux
        // qu'un badge qui ment.
        const avancement = {};
        const compteur = id =>
          (avancement[id] = avancement[id] || { definis: 0, joues: 0, finalesDefinies: 0 });

        const { data: matchsData, error: matchsError } = await supabase
          .from('match')
          .select('tournoi_id, phase_id, equipe1_texte, score_equipe1');

        if (matchsError) {
          console.error('Erreur matchs:', matchsError);
        } else {
          (matchsData || []).forEach(match => {
            if (!match.equipe1_texte || match.equipe1_texte.trim() === '') return;
            const c = compteur(match.tournoi_id);
            c.definis += 1;
            if (match.score_equipe1 !== null && match.score_equipe1 !== undefined) c.joues += 1;
            if (match.phase_id === 3) c.finalesDefinies += 1;
          });
        }

        const formatted = data.map(tournoi => {
          const c = avancement[tournoi.id] || { definis: 0, joues: 0, finalesDefinies: 0 };
          // « Termine » exige que les finales aient eu lieu : sans ce garde-fou, un
          // tournoi pas encore commence (aucun score, mais aucun match joue non plus)
          // basculerait a tort.
          const termine = c.definis > 0 && c.joues === c.definis && c.finalesDefinies > 0;
          const enFinales = tournoi.affichage_finales === true && c.finalesDefinies > 0;

          return {
            id: tournoi.id,
            name: tournoi.nom || tournoi.name || `Tournoi ${tournoi.id}`,
            slug: tournoi.slug || '',
            description: tournoi.description || '',
            termine,
            enFinales: enFinales && !termine,
          };
        });

        setTournaments(formatted);
      } catch (error) {
        console.error('Erreur lors de la récupération des tournois:', error);
        setError(error.message || 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    };

    fetchTournaments();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto px-5 py-10">
        <div className="card p-6 text-center">
          <h2 className="h-display text-xl text-brand-ink mb-2">Oups…</h2>
          <p className="text-sm text-brand-muted mb-1">Erreur lors du chargement des tournois : {error}</p>
          {errorDetails && <p className="text-xs text-brand-muted mb-4">Détails : {errorDetails}</p>}
          <button onClick={() => window.location.reload()} className="btn-primary mt-2">
            <i className="ri-refresh-line"></i> Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Le badge se meritait auparavant sur le nom du tournoi (/iguane|finale/), si bien
  // que les Iguanes affichaient « Finale » en permanence, poules comprises. Il suit
  // desormais l'etat reel, deduit des matchs : finales en cours, ou tournoi termine.
  const isFinale = (t) => t.enFinales === true;
  const isTermine = (t) => t.termine === true;

  return (
    <div className="max-w-md mx-auto pb-20">
      {/* Brand head */}
      <header className="relative bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white rounded-b-3xl overflow-hidden shadow-hero px-5 pt-8 pb-6 mb-5">
        <div className="absolute top-0 inset-x-0 h-2 hazard-white"></div>
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-white/85">Édition 2026</div>
        <h1 className="h-display text-4xl mt-2 leading-[0.95]">Les<br />Tournois</h1>
        <p className="text-sm text-white/90 mt-3 max-w-[16rem]">
          {tournaments.length > 0 ? `${tournaments.length} rendez-vous, un week-end. ` : ''}Choisissez votre compétition.
        </p>
        <img src={cone} alt="" aria-hidden="true" className="absolute right-4 bottom-3 w-16 opacity-95 drop-shadow-lg" />
      </header>

      {tournaments.length === 0 ? (
        <div className="px-5">
          <div className="card p-8 text-center flex flex-col items-center">
            <img src={cone} alt="" className="h-20 w-auto mb-4" />
            <h2 className="h-display text-xl mb-2 text-brand-ink">Aucun tournoi</h2>
            <p className="text-sm text-brand-muted">Les tournois apparaîtront ici dès qu'ils seront publiés.</p>
          </div>
        </div>
      ) : (
        <div className="px-5 flex flex-col gap-5">
          {tournaments.map((t, i) => {
            const band = TILE_BANDS[i % TILE_BANDS.length];
            const finale = isFinale(t);
            const termine = isTermine(t);
            const partner = partnerFor(t);
            const parts = (t.description || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);
            const day = parts.length ? parts[parts.length - 1] : '';
            const body = parts.length > 1 ? parts.slice(0, -1).join(' · ') : '';
            return (
              <button
                key={t.id}
                onClick={() => navigate(`/tournament/${t.id}`)}
                className="card overflow-hidden text-left active:scale-[.99] transition-transform"
              >
                <div className={`relative h-36 p-4 flex flex-col justify-between gap-2 bg-gradient-to-br ${band} overflow-hidden`}>
                  <img src={wordmarkLight} alt="" aria-hidden="true" className="absolute -right-6 -bottom-9 w-52 opacity-[0.14] pointer-events-none" />
                  {finale && <img src={cone} alt="" aria-hidden="true" className="absolute right-3 top-9 w-16 opacity-90 drop-shadow-lg" />}
                  {day && (
                    <span className="self-start bg-white/90 text-brand-orange-deep text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full relative z-10">
                      {day}
                    </span>
                  )}
                  {finale && (
                    <span className="self-start inline-flex items-center gap-1.5 bg-brand-ink text-[#FFD79E] text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full relative z-10">
                      <i className="ri-fire-fill"></i> Finale
                    </span>
                  )}
                  {termine && (
                    <span className="self-start inline-flex items-center gap-1.5 bg-white/25 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full relative z-10">
                      <i className="ri-check-double-line"></i> Terminé
                    </span>
                  )}
                  {partner && (
                    <span className="absolute right-3 bottom-3 z-10 inline-flex items-center bg-white rounded-xl px-2.5 py-2 shadow-card">
                      <img
                        src={partner.logo}
                        alt={`Partenaire ${partner.name}`}
                        className={`${partner.h} w-auto max-w-[140px] object-contain`}
                      />
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="h-display text-2xl text-brand-ink leading-none mb-2">{t.name}</h3>
                  {body && <p className="text-sm text-brand-muted leading-relaxed">{body}</p>}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-brand-line">
                    <span className="text-sm font-bold text-brand-orange">Voir le tournoi</span>
                    <span className="w-9 h-9 rounded-xl bg-brand-orange-soft text-brand-orange-deep flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-lg"></i>
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Tournaments;
